import api from "./api";

// ─── Types ───────────────────────────────────────────────────────────────────

interface DaySchedule {
  enabled: boolean; // Whether the user works this day
  from: string; // "HH:mm" format
  to: string; // "HH:mm" format
}

interface WeekSchedule {
  sunday?: DaySchedule;
  monday?: DaySchedule;
  tuesday?: DaySchedule;
  wednesday?: DaySchedule;
  thursday?: DaySchedule;
  friday?: DaySchedule;
  saturday?: DaySchedule;
}

interface TruckYardGeofence {
  id: string;
  name: string;
  latitude?: number;
  longitude?: number;
  radius?: number; // in meters
}

export interface AccessValidationResult {
  allowed: boolean;
  reason?:
    | "outside_geofence"
    | "outside_schedule"
    | "no_yard_assigned"
    | "geolocation_error";
  yardName?: string;
  scheduleInfo?: {
    from: string;
    to: string;
    currentTime: string;
  };
}

// ─── Haversine Distance Calculation ──────────────────────────────────────────

/**
 * Calculate the distance between two geographic coordinates using Haversine formula
 * @returns distance in meters
 */
function calculateHaversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

// ─── Schedule Validation ─────────────────────────────────────────────────────

/**
 * Get the English day name for current day (Mexico City timezone)
 */
function getCurrentDayName(): keyof WeekSchedule {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Mexico_City",
    weekday: "long",
  });
  const dayName = formatter.format(now);
  // Map English day name to our key
  const dayMap: Record<string, keyof WeekSchedule> = {
    sunday: "sunday",
    monday: "monday",
    tuesday: "tuesday",
    wednesday: "wednesday",
    thursday: "thursday",
    friday: "friday",
    saturday: "saturday",
  };
  return dayMap[dayName.toLowerCase()] || "monday";
}

/**
 * Parse time string "HH:mm" to minutes since midnight
 */
function parseTimeToMinutes(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

/**
 * Get current time in "HH:mm" format (Mexico City timezone)
 */
function getCurrentTime(): string {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Mexico_City",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const timeStr = formatter.format(now);
  return timeStr;
}

/**
 * Check if current time is within the user's schedule for today
 */
function isWithinSchedule(schedule: WeekSchedule | undefined): {
  allowed: boolean;
  scheduleInfo?: { from: string; to: string; currentTime: string };
} {
  if (!schedule) {
    // No schedule configured means no restrictions
    return { allowed: true };
  }

  const currentDay = getCurrentDayName();
  const daySchedule = schedule[currentDay];

  // Check if day exists and is enabled
  if (!daySchedule || daySchedule.enabled === false) {
    // Day not configured or disabled means user shouldn't work today
    return {
      allowed: false,
      scheduleInfo: {
        from: "No programado",
        to: "No programado",
        currentTime: getCurrentTime(),
      },
    };
  }

  // Check if from/to are configured
  if (!daySchedule.from || !daySchedule.to) {
    return {
      allowed: false,
      scheduleInfo: {
        from: "No configurado",
        to: "No configurado",
        currentTime: getCurrentTime(),
      },
    };
  }

  const currentTime = getCurrentTime();
  const currentMinutes = parseTimeToMinutes(currentTime);
  const fromMinutes = parseTimeToMinutes(daySchedule.from);
  const toMinutes = parseTimeToMinutes(daySchedule.to);

  // Handle overnight shifts (e.g., 22:00 to 06:00)
  let allowed: boolean;
  if (fromMinutes <= toMinutes) {
    // Normal shift (e.g., 08:00 to 16:00)
    allowed = currentMinutes >= fromMinutes && currentMinutes <= toMinutes;
  } else {
    // Overnight shift (e.g., 22:00 to 06:00)
    allowed = currentMinutes >= fromMinutes || currentMinutes <= toMinutes;
  }

  return {
    allowed,
    scheduleInfo: {
      from: daySchedule.from,
      to: daySchedule.to,
      currentTime,
    },
  };
}

// ─── Get User's Current Position ─────────────────────────────────────────────

/**
 * Request location permission explicitly
 * Returns true if permission is granted, false otherwise
 */
export async function requestLocationPermission(): Promise<boolean> {
  if (!navigator.geolocation) {
    return false;
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      () => resolve(true),
      (error) => {
        console.error("Location permission denied:", error);
        resolve(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  });
}

/**
 * Get user's current geolocation
 */
function getUserPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation not supported"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log("[Geolocation] User position obtained:", {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
        resolve(position);
      },
      reject,
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  });
}

// ─── Fetch Yard Data ─────────────────────────────────────────────────────────

/**
 * Fetch yard data by ID
 */
async function fetchYardById(
  yardId: string,
  token: string,
): Promise<TruckYardGeofence | null> {
  try {
    const response = await api.get("/truck-yard/yards", {
      params: { id: yardId },
      headers: { Authorization: `Bearer ${token}` },
    });

    const yards = response.data?.data || [];
    const yard = yards.find((y: any) => y.id === yardId);

    if (yard) {
      return {
        id: yard.id,
        name: yard.name,
        latitude: yard.latitude,
        longitude: yard.longitude,
        radius: yard.radius,
      };
    }
    return null;
  } catch (error) {
    console.error("Error fetching yard:", error);
    return null;
  }
}

// ─── Main Validation Function ────────────────────────────────────────────────

/**
 * Validate if a guard or supervisor can access the system based on:
 * 1. Geolocation (must be within yard's geofence)
 * 2. Schedule (must be within their work schedule)
 */
export async function validateGuardSupervisorAccess(
  userData: {
    yard_id?: string;
    yard_ids?: string[];
    schedule?: WeekSchedule;
  },
  token: string,
): Promise<AccessValidationResult> {
  // Get user's assigned yard ID
  const yardId =
    userData.yard_id || (userData.yard_ids && userData.yard_ids[0]);

  if (!yardId) {
    return {
      allowed: false,
      reason: "no_yard_assigned",
    };
  }

  // Fetch yard data to get geofence info
  const yard = await fetchYardById(yardId, token);

  if (!yard) {
    // If we can't fetch yard data, allow access (fail open)
    console.warn("Could not fetch yard data, allowing access");
    return { allowed: true };
  }

  // 1. Validate schedule first (doesn't require geolocation)
  const scheduleResult = isWithinSchedule(userData.schedule);

  if (!scheduleResult.allowed) {
    return {
      allowed: false,
      reason: "outside_schedule",
      yardName: yard.name,
      scheduleInfo: scheduleResult.scheduleInfo,
    };
  }

  // 2. Validate geolocation (only if yard has geofence configured)
  if (yard.latitude && yard.longitude && yard.radius) {
    try {
      const position = await getUserPosition();
      const userLat = position.coords.latitude;
      const userLon = position.coords.longitude;

      const distance = calculateHaversineDistance(
        userLat,
        userLon,
        Number(yard.latitude),
        Number(yard.longitude),
      );

      console.log("[Geofence Debug]", {
        userLat,
        userLon,
        yardLat: yard.latitude,
        yardLon: yard.longitude,
        yardRadius: yard.radius,
        distanceMeters: distance.toFixed(2),
        isInside: distance <= Number(yard.radius),
      });

      if (distance > Number(yard.radius)) {
        return {
          allowed: false,
          reason: "outside_geofence",
          yardName: yard.name,
        };
      }
    } catch (error) {
      console.error("Geolocation error:", error);
      return {
        allowed: false,
        reason: "geolocation_error",
        yardName: yard.name,
      };
    }
  }

  // All validations passed
  return { allowed: true };
}
